import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import Modal from "react-modal";
import { MutatingDots } from "react-loader-spinner";

const OrdersPage = () => {
  const [activeTab, setActiveTab] = useState("View all");
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const token = localStorage.getItem("AficFreshtoken");

  // Custom styles for the Modal
  const customModalStyles = {
    content: {
      maxWidth: '800px',
      width: '90%',
      maxHeight: '90vh',
      margin: 'auto',
      overflow: 'auto',
      padding: '0',
      border: 'none',
      borderRadius: '0.5rem',
      backgroundColor: 'white',
      position: 'relative',
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 999,
    }
  };

  // Fetch all orders for the authenticated user
  useEffect(() => {
    const fetchOrders = async () => {
      if (!token) {
        toast.error("You must be logged in to view orders.");
        return;
      }

      try {
        setIsLoading(true);
        const response = await axios.get(
          "https://afric-fresh-backend.onrender.com/user/orders",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setOrders(response.data.orders);
        setFilteredOrders(response.data.orders);
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to fetch orders.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, [token]);

  // Filter orders based on the active tab
  useEffect(() => {
    if (activeTab === "View all") {
      setFilteredOrders(orders);
    } else {
      const statusMap = {
        "Payment Pending": "pending payment",
        "Processed": "shipped",
        "Delivered": "completed",
      };
      setFilteredOrders(orders.filter((order) => order.orderStatus === statusMap[activeTab]));
    }
  }, [activeTab, orders]);

  // Open modal with selected order
  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedOrder(null);
  };

  // Render order details in modal
  const renderOrderDetails = () => {
    if (!selectedOrder) return null;

    return (
      <div className="relative">
        <button
          onClick={closeModal}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 z-10"
          aria-label="Close modal"
        >
          ✖
        </button>
        
        <div className="p-6 max-h-[90vh] overflow-y-auto">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Order Details</h2>
          <p>
            <strong>Order Number:</strong> {selectedOrder.orderNumber}
          </p>
          <p>
            <strong>Date:</strong> {new Date(selectedOrder.date).toLocaleDateString()}
          </p>
          <p>
            <strong>Total:</strong> ${selectedOrder.total}
          </p>
          <p>
            <strong>Status:</strong> {selectedOrder.orderStatus}
          </p>
          <p className="mt-4">
            <strong>Billing Address:</strong>
          </p>
          <p>
            {selectedOrder.billingAddress.firstName} {selectedOrder.billingAddress.lastName}
          </p>
          <p>{selectedOrder.billingAddress.streetHouseApartment}</p>
          <p>
            {selectedOrder.billingAddress.city}, {selectedOrder.billingAddress.state},{" "}
            {selectedOrder.billingAddress.zipCode}
          </p>
          <p>{selectedOrder.billingAddress.phone}</p>
          <p>{selectedOrder.billingAddress.email}</p>

          <h3 className="text-lg font-bold text-gray-800 mt-6">Products:</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {selectedOrder.cartItems.map((item) => (
              <div key={item.id} className="p-4 border rounded-lg">
                <img
                  src={item.img}
                  alt={item.name}
                  className="w-full h-32 object-cover rounded-md mb-2"
                />
                <p className="font-bold">{item.name}</p>
                <p>{item.brand}</p>
                <p>${item.price}</p>
                <p>Quantity: {item.quantity}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto mt-8 bg-white p-6 shadow-lg rounded-lg">
      {/* Navigation Tabs */}
      <div className="flex justify-between border-b-2 border-gray-200 mb-6">
        {["View all", "Payment Pending", "Processed", "Delivered"].map((tab) => (
          <button
            key={tab}
            className={`flex-1 py-3 text-center font-medium ${
              activeTab === tab
                ? "text-[#007D44] border-b-2 border-[#007D44]"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4 mb-6">
        <select className="p-2 border border-gray-300 rounded-md bg-gray-50">
          <option value="Order">Order</option>
        </select>
        <input
          type="text"
          placeholder="Order ID, product or store name"
          className="flex-1 p-2 border border-gray-300 rounded-md"
        />
        <button className="px-4 py-2 bg-[#007D44] text-white rounded-md hover:bg-blue-600">
          🔍
        </button>
        <select className="p-2 border border-gray-300 rounded-md bg-gray-50">
          <option value="last-year">All / Last year</option>
        </select>
      </div>

      {/* Table Heading */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border">
          <thead>
            <tr className="bg-gray-100 text-gray-700">
              <th className="py-3 px-6 text-left">Order Number</th>
              <th className="py-3 px-6 text-left">Date</th>
              <th className="py-3 px-6 text-left">Total</th>
              <th className="py-3 px-6 text-left">Order Status</th>
              <th className="py-3 px-6 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="5" className="py-6 text-center">
                  <MutatingDots
                    visible={true}
                    height="100"
                    width="100"
                    color="#4fa94d"
                    secondaryColor="#4fa94d"
                    radius="12.5"
                    ariaLabel="mutating-dots-loading"
                    wrapperStyle={{}}
                    wrapperClass=""
                  />
                </td>
              </tr>
            ) : filteredOrders.length === 0 ? (
              <tr>
                <td colSpan="5" className="py-6 text-center text-gray-600">
                  No orders found in this category.
                </td>
              </tr>
            ) : (
              filteredOrders.map((order, index) => (
                <tr
                  key={order.orderNumber}
                  className={`border-b hover:bg-gray-50 ${
                    index % 2 === 0 ? "bg-gray-50" : "bg-white"
                  }`}
                >
                  <td className="py-3 px-6">{order.orderNumber}</td>
                  <td className="py-3 px-6">
                    {new Date(order.date).toLocaleDateString()}
                  </td>
                  <td className="py-3 px-6">${order.total}</td>
                  <td className="py-3 px-6">{order.orderStatus}</td>
                  <td className="py-3 px-6 text-center">
                    <button
                      onClick={() => handleOrderClick(order)}
                      className="px-4 py-2 bg-[#007D44] text-white rounded-md hover:bg-[#005F34] transition duration-200"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        style={customModalStyles}
        contentLabel="Order Details"
      >
        {renderOrderDetails()}
      </Modal>
    </div>
  );
};

export default OrdersPage;